import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { NumberInput, toNumberValue } from '@/components/molecules/NumberInput';
import { Skeleton } from '@/components/atoms/skeleton';
import { Badge } from '@/components/atoms/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { useTaxRates } from '@/hooks/settings/useTaxRates';
import { useCreateTaxRate } from '@/hooks/settings/useCreateTaxRate';
import { useUpdateTaxRate } from '@/hooks/settings/useUpdateTaxRate';
import { useDeleteTaxRate } from '@/hooks/settings/useDeleteTaxRate';
import type { TaxRate } from '@/types/taxRate';

export const Route = createFileRoute('/_dashboard/dashboard/settings/taxes')({
  component: TaxRatesPage,
});

// ─── Schema ───────────────────────────────────────────────────────────────────

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  rate: z.number().min(0, 'Rate must be ≥ 0').max(100, 'Rate must be ≤ 100'),
  // NOT-7 — printed alongside the tax code on customer-facing documents.
  // 64-char limit mirrors the DB column; intentionally free-form to cover
  // BN/HST/GST/PST/EIN/VAT across jurisdictions.
  registrationNumber: z.string().trim().max(64, 'Must be 64 characters or fewer').optional(),
  isDefault: z.boolean().optional(),
});

type FormValues = z.infer<typeof schema>;

function toPercent(rate: number): number {
  return Number((rate * 100).toFixed(4));
}

function toDecimalRate(ratePercent: number): number {
  return Number((ratePercent / 100).toFixed(6));
}

// ─── Component ────────────────────────────────────────────────────────────────

function TaxRatesPage() {
  const { data: taxRates = [], isLoading } = useTaxRates();
  const createTaxRate = useCreateTaxRate();
  const updateTaxRate = useUpdateTaxRate();
  const deleteTaxRate = useDeleteTaxRate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TaxRate | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', rate: 0, registrationNumber: '', isDefault: false },
  });

  function openCreate() {
    setEditing(null);
    form.reset({ name: '', rate: 0, registrationNumber: '', isDefault: false });
    setDialogOpen(true);
  }

  function openEdit(tax: TaxRate) {
    setEditing(tax);
    form.reset({
      name: tax.name,
      rate: toPercent(tax.rate),
      registrationNumber: tax.registrationNumber ?? '',
      isDefault: tax.isDefault,
    });
    setDialogOpen(true);
  }

  function onSubmit(values: FormValues) {
    const trimmedRegNumber = values.registrationNumber?.trim() ?? '';
    const rate = toDecimalRate(values.rate);
    if (editing) {
      // Send null to clear an existing value; only send the field when the
      // user actually typed something or cleared it (avoid undefined → no-op
      // confusion). Per-tax-code registration number (NOT-7).
      const updateDto = {
        name: values.name,
        rate,
        isDefault: values.isDefault,
        registrationNumber: trimmedRegNumber.length > 0 ? trimmedRegNumber : null,
      };
      updateTaxRate.mutate(
        { id: editing.id, dto: updateDto },
        { onSuccess: () => setDialogOpen(false) },
      );
    } else {
      // Omit on create when blank so Prisma persists null rather than '' —
      // empty string would render a stray "Reg #: " line on the PDF.
      const createDto = {
        name: values.name,
        rate,
        isDefault: values.isDefault,
        ...(trimmedRegNumber.length > 0 ? { registrationNumber: trimmedRegNumber } : {}),
      };
      createTaxRate.mutate(createDto, {
        onSuccess: () => setDialogOpen(false),
      });
    }
  }

  const isPending = createTaxRate.isPending || updateTaxRate.isPending;

  if (isLoading) {
    return (
      <div className="max-w-2xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Tax Rates</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage tax rates applied to invoices and quotes.
          </p>
        </div>
        <Button size="sm" onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Rate
        </Button>
      </div>

      {taxRates.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-12 text-center">
          <p className="text-sm text-muted-foreground">No tax rates yet.</p>
          <Button variant="link" size="sm" className="mt-1" onClick={openCreate}>
            Add your first tax rate
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Registration #</TableHead>
                <TableHead>Default</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {taxRates.map((tax) => (
                <TableRow key={tax.id}>
                  <TableCell className="font-medium">{tax.name}</TableCell>
                  <TableCell>{toPercent(tax.rate)}%</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {tax.registrationNumber ?? '—'}
                  </TableCell>
                  <TableCell>
                    {tax.isDefault && (
                      <Badge variant="secondary" className="gap-1">
                        <Star className="h-3 w-3" /> Default
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEdit(tax)}
                        aria-label={`Edit ${tax.name} tax rate`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={() => deleteTaxRate.mutate(tax.id)}
                        disabled={deleteTaxRate.isPending}
                        aria-label={`Delete ${tax.name} tax rate`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Tax Rate' : 'Add Tax Rate'}</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="State Tax" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="rate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rate (%)</FormLabel>
                    <FormControl>
                      <NumberInput
                        step="0.01"
                        placeholder="8.25"
                        name={field.name}
                        ref={field.ref}
                        onBlur={field.onBlur}
                        value={toNumberValue(field.value)}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="registrationNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Registration Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. 123456789RT0001"
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Optional. Printed alongside this tax on customer-facing estimates and
                      invoices. Use the government-issued ID for this specific tax (GST, PST, VAT,
                      EIN, etc.).
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isDefault"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value ?? false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="h-4 w-4 rounded border-border"
                      />
                    </FormControl>
                    <FormLabel className="font-normal">Set as default tax rate</FormLabel>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? 'Saving…' : editing ? 'Save Changes' : 'Add Rate'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
